'use client';
import React, { useState, useRef, useEffect } from 'react';
import { X, Globe, Upload, Webhook, CheckCircle, AlertCircle, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

interface LeadSourceModalProps {
  onClose: () => void;
  onImported: () => void;
}

type Tab = 'api' | 'csv' | 'webhook';
type LeadSource = 'MBBS Lighthouse' | 'GMC' | 'Neet Navigator';

function parseCSV(text: string): Record<string, string>[] {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0]
    .split(',')
    .map((h) => h.trim().replace(/^"|"$/g, '').toLowerCase().replace(/\s+/g, '_'));
  return lines.slice(1).map((line) => {
    // Handle quoted fields with commas inside
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
      else { current += ch; }
    }
    values.push(current.trim());
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { row[h] = (values[i] ?? '').replace(/^"|"$/g, ''); });
    return row;
  });
}

function resolveSource(label: string): LeadSource {
  const n = (label ?? '').toLowerCase().trim();
  if (n.includes('lighthouse') || n.includes('mbbs')) return 'MBBS Lighthouse';
  if (n.includes('gmc')) return 'GMC';
  return 'Neet Navigator';
}

export default function LeadSourceModal({ onClose, onImported }: LeadSourceModalProps) {
  const [tab, setTab] = useState<Tab>('api');

  // API tab state
  const [apiUrl, setApiUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [headerName, setHeaderName] = useState('Authorization');
  const [apiSource, setApiSource] = useState('');
  const [apiLoading, setApiLoading] = useState(false);
  const [apiResult, setApiResult] = useState<{ inserted?: number; error?: string } | null>(null);

  // CSV tab state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvSource, setCsvSource] = useState('');
  const [csvPreview, setCsvPreview] = useState<Record<string, string>[]>([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvResult, setCsvResult] = useState<{ inserted?: number; error?: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Webhook tab state
  const [copied, setCopied] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('/api/leads/webhook');
  React.useEffect(() => {
    setWebhookUrl(`${window.location.origin}/api/leads/webhook`);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // API fetch handler
  const handleApiFetch = async () => {
    if (!apiUrl || !apiSource) { toast.error('Endpoint URL and Source Label are required'); return; }
    setApiLoading(true);
    setApiResult(null);
    try {
      const res = await fetch('/api/leads/fetch-external', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint_url: apiUrl, api_key: apiKey || undefined, header_name: headerName || undefined, source_label: apiSource }),
      });
      const data = await res.json();
      setApiResult(data);
      if (!data.error) { toast.success(data.message); onImported(); }
      else toast.error(data.error);
    } catch {
      setApiResult({ error: 'Network error' });
      toast.error('Failed to connect to external API');
    }
    setApiLoading(false);
  };

  // CSV file handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    setCsvResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const rows = parseCSV(text);
      setCsvPreview(rows.slice(0, 3));
    };
    reader.readAsText(file);
  };

  // CSV import — direct client-side Supabase insert (bypasses API route, uses auth session)
  const handleCsvImport = async () => {
    if (!csvFile) { toast.error('Please select a CSV file'); return; }
    if (!csvSource) { toast.error('Please select a Source Label'); return; }

    setCsvLoading(true);
    setCsvResult(null);

    try {
      const text = await csvFile.text();
      const rows = parseCSV(text);

      if (!rows.length) {
        toast.error('No valid rows found in CSV');
        setCsvLoading(false);
        return;
      }

      const resolvedSource = resolveSource(csvSource);

      const mapped = rows
        .filter((r) => r.name || r.full_name || r.student_name)
        .map((r) => ({
          name: (r.name || r.full_name || r.student_name || 'Unknown').trim(),
          phone: (r.phone || r.mobile || r.contact || '').trim(),
          email: (r.email || r.email_address || '').trim() || null,
          neet_score: parseInt(r.neet_score || r.score || '0', 10) || 0,
          source: resolvedSource,
          stage: 'New' as const,
          counselor: 'Unassigned',
          budget: (r.budget || '').trim() || null,
          location: (r.location || r.city || 'India').trim(),
          last_activity: 'Imported via CSV',
          is_duplicate: false,
          opted_in: true,
        }));

      if (!mapped.length) {
        toast.error('No rows with a valid name column found. Ensure your CSV has a "name" column.');
        setCsvLoading(false);
        return;
      }

      const supabase = createClient();

      // Insert in batches of 50 to avoid payload limits
      const BATCH = 50;
      let totalInserted = 0;
      for (let i = 0; i < mapped.length; i += BATCH) {
        const batch = mapped.slice(i, i + BATCH);
        const { data, error } = await supabase.from('leads').insert(batch).select('id');
        if (error) {
          setCsvResult({ error: error.message });
          toast.error(`Import failed: ${error.message}`);
          setCsvLoading(false);
          return;
        }
        totalInserted += data?.length ?? 0;
      }

      setCsvResult({ inserted: totalInserted });
      toast.success(`${totalInserted} leads imported successfully`);
      onImported();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unexpected error during import';
      setCsvResult({ error: msg });
      toast.error(msg);
    }

    setCsvLoading(false);
  };

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'api', label: 'REST API', icon: <Globe size={15} /> },
    { id: 'csv', label: 'CSV Import', icon: <Upload size={15} /> },
    { id: 'webhook', label: 'Webhook', icon: <Webhook size={15} /> },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border">
          <div>
            <h2 className="text-base font-bold text-foreground">Import Leads</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Connect your external lead sources</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-4">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg transition-all ${
                tab === t.id
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="px-6 py-5 space-y-4">

          {/* REST API Tab */}
          {tab === 'api' && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Fetch leads from your website&apos;s REST API endpoint. The CRM will call the URL and import all returned leads.</p>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-foreground">API Endpoint URL *</label>
                <input
                  type="url"
                  placeholder="https://yourwebsite.com/api/leads"
                  value={apiUrl}
                  onChange={(e) => setApiUrl(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-foreground">Auth Header Name</label>
                  <input
                    type="text"
                    placeholder="Authorization"
                    value={headerName}
                    onChange={(e) => setHeaderName(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-foreground">API Key / Token</label>
                  <input
                    type="password"
                    placeholder="Bearer token or key"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-foreground">Source Label *</label>
                <select
                  value={apiSource}
                  onChange={(e) => setApiSource(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select source…</option>
                  <option value="MBBS Lighthouse">MBBS Lighthouse</option>
                  <option value="GMC">GMC</option>
                  <option value="Neet Navigator">Neet Navigator</option>
                </select>
              </div>
              {apiResult && (
                <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${apiResult.error ? 'bg-red-50 text-danger border border-red-200' : 'bg-green-50 text-success border border-green-200'}`}>
                  {apiResult.error ? <AlertCircle size={13} /> : <CheckCircle size={13} />}
                  {apiResult.error ?? `${apiResult.inserted} leads imported successfully`}
                </div>
              )}
              <button
                onClick={handleApiFetch}
                disabled={apiLoading}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-60 transition-colors"
              >
                {apiLoading ? <Loader2 size={15} className="animate-spin" /> : <Globe size={15} />}
                {apiLoading ? 'Fetching Leads…' : 'Fetch & Import Leads'}
              </button>
            </div>
          )}

          {/* CSV Import Tab */}
          {tab === 'csv' && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Upload a CSV file with columns: <span className="font-mono text-foreground">name, phone, email, neet_score, budget, location</span>. The &quot;name&quot; column is required.
              </p>
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all"
              >
                <Upload size={22} className="mx-auto text-muted-foreground mb-2" />
                {csvFile ? (
                  <p className="text-sm font-semibold text-foreground">{csvFile.name}</p>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-foreground">Click to upload CSV file</p>
                    <p className="text-xs text-muted-foreground mt-1">MySQL / PostgreSQL exports supported</p>
                  </>
                )}
                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFileChange} />
              </div>

              {csvPreview.length > 0 && (
                <div className="bg-muted/40 rounded-lg p-3 space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">Preview (first {csvPreview.length} rows)</p>
                  {csvPreview.map((row, i) => (
                    <div key={i} className="text-xs text-foreground bg-card border border-border rounded px-2 py-1 truncate">
                      {Object.entries(row).slice(0, 4).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-xs font-semibold text-foreground">Source Label *</label>
                <select
                  value={csvSource}
                  onChange={(e) => setCsvSource(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select source…</option>
                  <option value="MBBS Lighthouse">MBBS Lighthouse</option>
                  <option value="GMC">GMC</option>
                  <option value="Neet Navigator">Neet Navigator</option>
                </select>
              </div>

              {csvResult && (
                <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${csvResult.error ? 'bg-red-50 text-danger border border-red-200' : 'bg-green-50 text-success border border-green-200'}`}>
                  {csvResult.error ? <AlertCircle size={13} /> : <CheckCircle size={13} />}
                  {csvResult.error ?? `${csvResult.inserted} leads imported successfully`}
                </div>
              )}

              <button
                onClick={handleCsvImport}
                disabled={csvLoading || !csvFile}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-60 transition-colors"
              >
                {csvLoading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                {csvLoading ? 'Importing…' : 'Import CSV'}
              </button>
            </div>
          )}

          {/* Webhook Tab */}
          {tab === 'webhook' && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">Add this webhook URL to your website&apos;s form submission handler. Every new lead submission will be pushed to the CRM in real-time.</p>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-foreground">Your Webhook Endpoint</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 text-xs bg-muted border border-border rounded-lg font-mono text-foreground truncate">
                    {webhookUrl}
                  </code>
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1 px-3 py-2 text-xs font-semibold border border-border rounded-lg hover:bg-muted transition-colors shrink-0"
                  >
                    {copied ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              <div className="bg-muted/40 border border-border rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-foreground">Expected JSON Payload</p>
                <pre className="text-xs text-muted-foreground font-mono leading-relaxed overflow-x-auto">{`POST ${webhookUrl}
Content-Type: application/json

{
  "name": "Rahul Sharma",
  "phone": "9876543210",
  "email": "rahul@example.com",
  "neet_score": 620,
  "source": "Website Name",
  "location": "Mumbai"
}`}</pre>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 space-y-1">
                <p className="text-xs font-semibold text-amber-800">Optional Security</p>
                <p className="text-xs text-amber-700">Set <code className="font-mono bg-amber-100 px-1 rounded">LEAD_WEBHOOK_SECRET</code> in your environment variables and pass it as the <code className="font-mono bg-amber-100 px-1 rounded">x-webhook-secret</code> header from your website to authenticate incoming pushes.</p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-card border border-border rounded-lg p-3">
                  <p className="font-semibold text-foreground mb-1">Accepts</p>
                  <p className="text-muted-foreground">Single lead object or array of leads</p>
                </div>
                <div className="bg-card border border-border rounded-lg p-3">
                  <p className="font-semibold text-foreground mb-1">Response</p>
                  <p className="text-muted-foreground font-mono">{"{ received: N }"}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
